import { useEffect } from 'react';

import { ChronicDiseaseSimple } from '@dreryk/egabinet-patient-medical-information-frontend-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import {
   FormController,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalWidth,
   Scroll,
   StyledFlex,
} from '@dreryk/react-components';

import { visitTranslationKeys } from '../../translations';
import { useVisitFormControllerRegistration } from '../../common/visitForm/VisitFormContext';
import { useVisitFormController } from '../form/visitForm.hooks';
import { VisitFormSynchronization } from '../form/VisitFormSynchronization';

import { DiagnosesField } from './fields/DiagnosesField';
import { InterviewField } from './fields/InterviewField';
import { ProceduresField } from './fields/ProceduresField';
import { RecommendationsField } from './fields/RecommendationsField';
import { SubjectiveAssessmentField } from './fields/SubjectiveAssessmentField';
import { VisitSettlementField } from './fields/VisitSettlementField';
import { PrescriptionVisitDataSaveAndCloseAction } from './PrescriptionVisitDataSaveAndCloseAction';

type PrescriptionVisitDataModalProps = {
   readonly visit: Visit;
   readonly chronicDiseases: ChronicDiseaseSimple[];
   readonly validateOnOpen: boolean;
   readonly onClose: VoidFunction;
};

export const PrescriptionVisitDataModal = (props: PrescriptionVisitDataModalProps): JSX.Element => {
   const controller = useVisitFormController(props.visit);
   const registerController = useVisitFormControllerRegistration();

   useEffect(() => {
      registerController(controller);

      return () => registerController(null);
   }, [registerController, controller]);

   useEffect(() => {
      if (props.validateOnOpen) {
         queueMicrotask(() => {
            controller.trigger();
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      <Modal
         testId={'prescriptionVisitData'}
         title={visitTranslationKeys.actionBar.prescriptionVisitData.label}
         width={ModalWidth.WIDE}
      >
         <Scroll testId={'prescriptionVisitDataContent'}>
            <StyledFlex direction={'column'} gap={16}>
               <InterviewField
                  controller={controller.register('interview')}
                  practitionerId={props.visit.practitionerId}
                  visitId={props.visit.id}
               />
               <SubjectiveAssessmentField
                  controller={controller.register('subjectiveAssessments')}
                  practitionerId={props.visit.practitionerId}
                  visitId={props.visit.id}
               />
               <RecommendationsField
                  controller={controller.register('recommendations')}
                  practitionerId={props.visit.practitionerId}
                  visitId={props.visit.id}
                  patientId={props.visit.patient.id}
               />
               <DiagnosesField
                  register={controller.register}
                  visitId={props.visit.id}
                  dictionaryValidOn={props.visit.beginDate}
                  chronicDiseases={props.chronicDiseases}
               />
               <ProceduresField register={controller.register} dictionaryValidOn={props.visit.beginDate} />
               <VisitSettlementField controller={controller} visit={props.visit} />
            </StyledFlex>
         </Scroll>
         <FormController controller={controller.register('procedures')} />
         <FormController controller={controller.register('diagnoses')} />
         <VisitFormSynchronization
            controller={controller}
            chronicDiseases={props.chronicDiseases}
            visitId={props.visit.id}
         />
         <PrescriptionVisitDataSaveAndCloseAction
            controller={controller}
            visitId={props.visit.id}
            appointmentId={props.visit.appointmentId}
            onClose={props.onClose}
         />
         <ModalCancelButton onClick={props.onClose} shouldConfirmExit={controller.formState.isDirty} />
         <ModalCloseButton onClick={props.onClose} shouldConfirmExit={controller.formState.isDirty} />
      </Modal>
   );
};
